import { SupportedLocale } from './supported-locales';

/**
 * Locales accepted by Auth0 universal login (`enabled_locales` and
 * `PUT /api/v2/prompts/{prompt}/custom-text/{language}`).
 *
 * Mirrors the `ENABLED_LOCALES` configured on the Auth0 tenant. Some entries
 * are language-only (`ja`, `de`, `ko`) and some include a region (`zh-CN`,
 * `fr-FR`). Anything outside this set causes Auth0 to fall back to English.
 */
const AUTH0_SUPPORTED_LOCALES: ReadonlySet<string> = new Set([
  'en',
  'ar',
  'ca-ES',
  'zh-CN',
  'zh-TW',
  'da',
  'nl',
  'fi',
  'fr-CA',
  'fr-FR',
  'de',
  'it',
  'ja',
  'ko',
  'no',
  'pl',
  'pt-PT',
  'es',
  'sv',
]);

/**
 * Maps an in-app {@link SupportedLocale} (BCP-47, e.g. `ja-JP`) to a single
 * value accepted by Auth0's `ui_locales` parameter.
 *
 * Auth0 only honours codes in its enabled_locales list. Most app locales use
 * the full `xx-YY` form (e.g. `ja-JP`, `de-DE`), which Auth0 does not accept
 * — those silently fall back to English on the universal login page.
 *
 * Note on space-separated `ui_locales`: although OIDC Section 3.1.2.1 defines
 * `ui_locales` as an ordered space-separated preference list, Auth0's
 * universal login does not iterate the list to find a supported tag — it
 * uses the head tag and falls back to the tenant default if it isn't in
 * enabled_locales. We therefore resolve to a single supported code here.
 *
 * Resolution order:
 *   1. The exact input if Auth0 supports it (e.g. `zh-CN`, `fr-FR`).
 *   2. The language subtag if Auth0 supports it (e.g. `ja-JP` -> `ja`).
 *   3. `en` as final fallback.
 *
 * Examples:
 *   `zh-CN`        -> `zh-CN`
 *   `ja-JP`        -> `ja`
 *   `ar-SA`        -> `ar`           (Auth0 supports the `ar` language subtag)
 *   `en-psaccent`  -> `en`           (dev-only pseudolocale)
 */
export const toAuth0UiLocales = (locale: SupportedLocale | string): string => {
  if ((locale as SupportedLocale) === SupportedLocale.EnPs) {
    return SupportedLocale.En;
  }

  // App locale nb-NO (Bokmål); Auth0 enabled_locales uses the `no` language tag.
  if ((locale as SupportedLocale) === SupportedLocale.NbNo) {
    return 'no';
  }

  if (AUTH0_SUPPORTED_LOCALES.has(locale)) {
    return locale;
  }

  const language = locale.split('-')[0];
  if (language && AUTH0_SUPPORTED_LOCALES.has(language)) {
    return language;
  }

  return SupportedLocale.En;
};
